// Entrée micro : capture par AudioWorklet, détection de hauteur et d'attaques sur
// l'appareil (PitchTracker). Rien n'est envoyé hors du navigateur.
//
// La même classe sert de « source de synthèse » : au lieu du micro, des sons type
// piano synthétisés sont injectés dans la même chaîne d'analyse. Cela permet de
// vérifier toute la chaîne (worklet, sous-échantillonnage, détection, appariement)
// sans piano ni micro. Ce n'est pas un substitut à l'essai sur un vrai piano.
//
// Ordre de démarrage, important sur iPhone et iPad : le contexte audio est créé et
// repris, et la demande d'autorisation du micro est lancée, dans le geste de
// l'utilisateur (clic ou touche), avant toute attente ; le reste se prépare ensuite.

import { PitchTracker } from '../engine/pitch/tracker';
import { synthesizeTone } from '../engine/synth';
import { midiToFrequency } from '../music/pitchNames';
import type { InputKind } from '../engine/types';
import { audioBlockPerfTime, compensateLatency } from './timing';
import { BaseInputSource, now, type DeviceInfo, type StatusCode } from './types';

export interface MicOptions {
  deviceId?: string | null;
  /** Compensation de latence (ms) soustraite à l'instant des attaques. */
  latencyMs: number;
  /** Source de synthèse au lieu du micro. */
  synthetic?: boolean;
}

interface CaptureMessage {
  samples: Float32Array;
  time: number;
  sampleRate: number;
}

/** États d'un AudioContext, dont « interrupted » propre à iOS (appel, autre application audio). */
type ContextState = AudioContextState | 'interrupted';

export function isSecureContextNow(): boolean {
  return typeof window === 'undefined' || window.isSecureContext !== false;
}

/** Message et code d'erreur pour une exception de getUserMedia. */
export function describeMicError(err: unknown): { code: StatusCode; message: string } {
  const e = err as { name?: string; message?: string };
  if (e?.name === 'NotAllowedError' || e?.name === 'SecurityError' || e?.name === 'PermissionDeniedError') {
    return { code: 'denied', message: 'Accès au micro refusé. Autorisez le micro pour ce site dans le navigateur, puis réessayez.' };
  }
  if (e?.name === 'NotFoundError' || e?.name === 'DevicesNotFoundError' || e?.name === 'OverconstrainedError') return { code: 'notFound', message: 'Aucun micro trouvé sur cet appareil.' };
  if (e?.name === 'NotReadableError' || e?.name === 'AbortError' || e?.name === 'TrackStartError') return { code: 'busy', message: 'Le micro est utilisé par une autre application, ou n’a pas pu démarrer.' };
  return { code: 'error', message: `Impossible de démarrer le micro (${e?.name || 'erreur'} : ${e?.message ?? ''}).` };
}

export class MicInputSource extends BaseInputSource {
  private ctx: AudioContext | null = null;
  private stream: MediaStream | null = null;
  private node: AudioWorkletNode | null = null;
  private tracker: PitchTracker | null = null;
  private synthGain: GainNode | null = null;
  private levelTimer: number | null = null;
  private latencyMs: number;
  private readonly deviceId: string | null;
  readonly synthetic: boolean;
  private activeMessage = '';
  private visibilityListener: (() => void) | null = null;

  constructor(options: MicOptions) {
    super((options.synthetic ? 'synth' : 'mic') as InputKind);
    this.latencyMs = options.latencyMs;
    this.deviceId = options.deviceId ?? null;
    this.synthetic = options.synthetic ?? false;
  }

  static isSupported(): boolean {
    return (
      typeof navigator !== 'undefined' &&
      !!navigator.mediaDevices &&
      typeof navigator.mediaDevices.getUserMedia === 'function' &&
      typeof AudioContext !== 'undefined' &&
      'audioWorklet' in AudioContext.prototype
    );
  }

  static async listDevices(): Promise<DeviceInfo[]> {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.enumerateDevices) return [];
    const all = await navigator.mediaDevices.enumerateDevices();
    return all.filter((d) => d.kind === 'audioinput').map((d, i) => ({ id: d.deviceId, name: d.label || `Micro ${i + 1}` }));
  }

  setLatency(ms: number): void {
    this.latencyMs = ms;
  }

  get tracking(): PitchTracker | null {
    return this.tracker;
  }

  async start(): Promise<void> {
    if (typeof AudioContext === 'undefined' || !('audioWorklet' in AudioContext.prototype)) {
      this.setStatus({ state: 'unsupported', code: 'unsupported', message: 'Ce navigateur ne permet pas l’analyse audio en temps réel (AudioWorklet).' });
      return;
    }
    if (!this.synthetic && !MicInputSource.isSupported()) {
      const insecure = !isSecureContextNow();
      this.setStatus({
        state: 'unsupported',
        code: insecure ? 'insecure' : 'unsupported',
        message: insecure
          ? 'La page n’est pas ouverte en HTTPS : le navigateur cache le micro. Ouvrez l’adresse qui commence par https://.'
          : 'Ce navigateur ne donne pas accès au micro. Essayez une version récente de Safari, Chrome, Edge ou Firefox.',
      });
      return;
    }
    this.setStatus({ state: 'starting', code: undefined, message: this.synthetic ? 'Démarrage de la source de synthèse…' : 'Demande d’accès au micro…' });
    let streamPromise: Promise<MediaStream> | null = null;
    try {
      const ctx = new AudioContext({ latencyHint: 'interactive' });
      this.ctx = ctx;
      // Reprise du contexte et demande d'autorisation lancées immédiatement, dans le geste.
      const resumed = ctx.resume();
      if (!this.synthetic) {
        streamPromise = navigator.mediaDevices.getUserMedia({
          audio: {
            deviceId: this.deviceId ? { exact: this.deviceId } : undefined,
            echoCancellation: false,
            noiseSuppression: false,
            autoGainControl: false,
            channelCount: 1,
          },
          video: false,
        });
        streamPromise.catch(() => undefined);
      }
      await resumed;
      if (streamPromise) {
        this.stream = await streamPromise;
        streamPromise = null;
      }
      await ctx.audioWorklet.addModule(`${import.meta.env.BASE_URL}worklets/capture-processor.js`);
      const node = new AudioWorkletNode(ctx, 'capture-processor', { numberOfInputs: 1, numberOfOutputs: 1, channelCount: 1 });
      this.node = node;
      // Le nœud doit être relié à la sortie pour être exécuté ; le gain nul évite tout retour.
      const sink = ctx.createGain();
      sink.gain.value = 0;
      node.connect(sink);
      sink.connect(ctx.destination);

      let processing: { echoCancellation?: boolean; noiseSuppression?: boolean; autoGainControl?: boolean; sampleRate?: number } = { sampleRate: ctx.sampleRate };
      let inputLatencyMs: number | undefined;
      if (this.synthetic) {
        const gain = ctx.createGain();
        gain.gain.value = 1;
        gain.connect(node);
        const monitor = ctx.createGain();
        monitor.gain.value = 0.2;
        gain.connect(monitor);
        monitor.connect(ctx.destination);
        this.synthGain = gain;
      } else if (this.stream) {
        const stream = this.stream;
        const source = ctx.createMediaStreamSource(stream);
        source.connect(node);
        const track = stream.getAudioTracks()[0];
        if (track) {
          const s = track.getSettings() as MediaTrackSettings & { latency?: number };
          processing = { echoCancellation: s.echoCancellation, noiseSuppression: s.noiseSuppression, autoGainControl: s.autoGainControl, sampleRate: ctx.sampleRate };
          if (typeof s.latency === 'number' && Number.isFinite(s.latency)) inputLatencyMs = Math.round(s.latency * 1000);
          track.onended = () => {
            this.setStatus({ state: 'lost', code: 'lost', message: 'Le micro a été déconnecté.' });
            this.emit({ type: 'connection', state: 'lost', time: now(), source: this.kind, detail: 'micro déconnecté' });
          };
          // iOS coupe la piste (sans la terminer) quand la page passe en arrière-plan ou pendant un appel.
          track.onmute = () => this.setInterrupted('Le micro est suspendu par le système (page en arrière-plan, appel ou autre application audio).');
          track.onunmute = () => this.setResumed();
        }
      }
      ctx.onstatechange = () => {
        const state = ctx.state as ContextState;
        if (this.ctx !== ctx) return;
        if (state === 'interrupted' || state === 'suspended') this.setInterrupted('L’analyse audio est suspendue par le système. Revenez sur cette page pour reprendre.');
        else if (state === 'running') this.setResumed();
      };
      if (typeof document !== 'undefined') {
        this.visibilityListener = () => {
          if (document.visibilityState === 'visible' && this.ctx === ctx && ctx.state !== 'running' && ctx.state !== 'closed') void ctx.resume().catch(() => undefined);
        };
        document.addEventListener('visibilitychange', this.visibilityListener);
      }
      this.tracker = new PitchTracker({ sampleRate: ctx.sampleRate });
      node.port.onmessage = (ev: MessageEvent<CaptureMessage>) => this.onChunk(ev.data);
      this.levelTimer = window.setInterval(() => {
        if (this.tracker) this.setStatus({ level: { db: this.tracker.levelDb, noise: this.tracker.noiseFloorDb } });
      }, 100);
      const devices = this.synthetic ? [] : await MicInputSource.listDevices();
      this.activeMessage = this.synthetic ? 'Source de synthèse active (sons type piano injectés dans l’analyse).' : 'Micro actif. Jouez une note pour vérifier la détection.';
      this.setStatus({
        state: 'active',
        code: undefined,
        message: this.activeMessage,
        devices,
        processing,
        inputLatencyMs,
        activeDevice: this.synthetic ? 'synthèse' : devices.find((d) => d.id === this.deviceId)?.name ?? devices[0]?.name,
      });
    } catch (err) {
      // Un flux obtenu après l'échec d'une autre étape doit aussi être libéré.
      streamPromise?.then((s) => s.getTracks().forEach((t) => t.stop()), () => undefined);
      const { code, message } = describeMicError(err);
      this.stop();
      this.setStatus({ state: 'error', code, message });
    }
  }

  stop(): void {
    if (this.levelTimer !== null) {
      window.clearInterval(this.levelTimer);
      this.levelTimer = null;
    }
    if (this.visibilityListener && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.visibilityListener);
      this.visibilityListener = null;
    }
    if (this.node) {
      this.node.port.onmessage = null;
      this.node.disconnect();
      this.node = null;
    }
    if (this.stream) {
      for (const t of this.stream.getTracks()) {
        t.onended = null;
        t.onmute = null;
        t.onunmute = null;
        t.stop();
      }
      this.stream = null;
    }
    if (this.ctx) {
      this.ctx.onstatechange = null;
      void this.ctx.close().catch(() => undefined);
      this.ctx = null;
    }
    this.synthGain = null;
    this.tracker = null;
    this.setStatus({ state: 'idle', code: undefined, message: '', level: undefined, activeDevice: undefined });
  }

  private setInterrupted(message: string): void {
    if (this.status.state !== 'active') return;
    this.setStatus({ state: 'lost', code: 'interrupted', message });
    this.emit({ type: 'connection', state: 'lost', time: now(), source: this.kind, detail: 'micro suspendu' });
  }

  private setResumed(): void {
    if (this.status.state !== 'lost' || this.status.code !== 'interrupted') return;
    const stream = this.stream;
    if (stream && stream.getAudioTracks().some((t) => t.muted)) return;
    this.setStatus({ state: 'active', code: undefined, message: this.activeMessage });
    this.emit({ type: 'connection', state: 'restored', time: now(), source: this.kind, detail: 'micro repris' });
  }

  private onChunk(data: CaptureMessage): void {
    const ctx = this.ctx;
    const tracker = this.tracker;
    if (!ctx || !tracker) return;
    // Instant (horloge performance.now) du premier échantillon du paquet. Le détecteur date
    // ensuite chaque attaque à sa position dans le flux : seule la latence d'entrée de
    // l'appareil reste à compenser, une seule fois, ici.
    const perfStart = audioBlockPerfTime(now(), ctx.currentTime, data.time);
    for (const e of tracker.process(data.samples, perfStart)) {
      if (e.type === 'noteOn') {
        this.emit({
          type: 'noteOn',
          pitch: e.pitch,
          time: compensateLatency(e.time, this.latencyMs),
          velocity: Math.max(0.05, Math.min(1, (e.levelDb + 60) / 60)),
          confidence: e.confidence,
          source: this.kind,
          cents: e.cents,
          clarity: e.clarity,
        });
      } else if (e.type === 'noteOff') {
        this.emit({ type: 'noteOff', pitch: e.pitch, time: compensateLatency(e.time, this.latencyMs), source: this.kind });
      } else {
        this.emit({ type: 'onset', time: compensateLatency(e.time, this.latencyMs), source: this.kind, detail: 'attaque sans hauteur reconnue' });
      }
    }
  }

  /** Source de synthèse : joue une note type piano dans la chaîne d'analyse. */
  playSyntheticNote(pitch: number, durationSec = 1.2, amplitude = 0.5): void {
    const ctx = this.ctx;
    if (!ctx || !this.synthGain) return;
    const samples = synthesizeTone(midiToFrequency(pitch), durationSec, ctx.sampleRate, { amplitude, seed: pitch });
    const buffer = ctx.createBuffer(1, samples.length, ctx.sampleRate);
    const copy = new Float32Array(samples.length);
    copy.set(samples);
    buffer.copyToChannel(copy, 0);
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    src.connect(this.synthGain);
    src.start();
  }
}
